import {
  BulletinPatch,
  deleteBulletinId,
  patchBulletinId,
} from "@rubi/api-generated";

import { toBulletinPatch } from "@/components/alerts/add-v2/model/adapters";
import { BulletinFormValues } from "@/components/alerts/add-v2/model/types";

import { BulletinRow } from "./bulletinRowContext";

/**
 * The document half of persistence: the whole form, sent to `PATCH /bulletin/{id}`.
 *
 * Nothing here is mocked. It replaced `mockRequest.ts`, which logged and resolved — that stand-in
 * was what let phases 1 to 5 be built before the endpoints existed, and keeping it any longer would
 * have meant a flow whose media was real and whose fields were imaginary.
 *
 * The split from `mediaRepository.ts` is the one in decision 21: bytes go to the attachment
 * endpoints, the document goes here, and both address the same row.
 *
 * ## Draft and published are the same document
 *
 * `is_draft` is not in the form model, because what the officer is writing is the same either way.
 * It is this file's business, and there are exactly three states it can put on the wire:
 *
 * | Call         | `is_draft` sent | What the endpoint does                               |
 * | ------------ | --------------- | ---------------------------------------------------- |
 * | `save` draft | `true`          | stays a draft                                        |
 * | `save` live  | *omitted*       | leaves the state alone — an edit is not a transition |
 * | `finalize`   | `false`         | publishes, and sends the notification emails         |
 *
 * The omission is the load-bearing one: `r_bulletins.py` guards the whole block with
 * `if update.is_draft is not None`, so a missing key means "do not touch this". Sending `false`
 * would work today too, but it says "publish this" to an endpoint that only happens not to act on
 * it — and the day that changes, every autosave on a live bulletin would re-send its emails.
 */

export interface SavedBulletin {
  /** The bulletin row's id. A `number` because that is what every `/bulletin/{id}` route takes. */
  id: number;
  savedAt: Date;
}

export interface BulletinRepository {
  /**
   * Whole-document save — decision 16 — which **leaves the published/draft state as it found it**.
   * Called by `useAutosave`, never by a component.
   */
  save(values: BulletinFormValues): Promise<SavedBulletin>;
  /**
   * Draft → published: `is_draft` goes false and the notification emails go out. Only reachable on
   * a draft, because it is the only thing that has somewhere to go.
   */
  finalize(values: BulletinFormValues): Promise<SavedBulletin>;
  /** Throws the row away. What "Discard draft" calls, and only offered on a draft. */
  discard(): Promise<void>;
}

const patchDocument = async (
  row: BulletinRow,
  values: BulletinFormValues,
  isDraft: boolean | undefined,
): Promise<SavedBulletin> => {
  // The id comes from the holder, never from a response. It dedupes concurrent creates and is the
  // same id the media uploads used, so one form instance stays one bulletin row (decision 21).
  const bulletinId = await row.ensureId();

  const body: BulletinPatch = {
    ...toBulletinPatch(values),
    ...(isDraft === undefined ? {} : { is_draft: isDraft }),
  };
  const { data, error } = await patchBulletinId({
    path: { bulletin_id: bulletinId },
    body,
  });

  // The response is the whole `BulletinResource`, and it is deliberately dropped. Applying it back
  // into the form would overwrite whatever was typed while the request was in flight, and the one
  // part of it the form does treat as authoritative — the media list, decision 22 — is reconciled
  // by `mediaRepository` against the response of the call that changed it.
  if (!data) throw error ?? new Error("Failed to save the bulletin");

  return { id: bulletinId, savedAt: new Date() };
};

/**
 * `published` is the bulletin's state as it was loaded, and it never changes for the life of a form
 * instance: finalizing navigates away, so a form that starts on a draft ends on one.
 */
export const createBulletinRepository = (
  row: BulletinRow,
  published = false,
): BulletinRepository => ({
  save: (values) => patchDocument(row, values, published ? undefined : true),

  /**
   * One-way: the endpoint refuses to put a published bulletin back into draft. So once this
   * resolves, `save` can no longer send `is_draft: true` for this row — which is why the page turns
   * autosave off before calling it and back on only if it fails (decision 38).
   */
  finalize: (values) => patchDocument(row, values, false),

  discard: async () => {
    const bulletinId = row.id();
    // Nothing was ever created, so there is nothing to throw away. Discarding a form that has not
    // saved yet is not an error to the officer, and creating a row in order to delete it would be
    // absurd.
    if (bulletinId === null) return;

    const { error } = await deleteBulletinId({
      path: { bulletin_id: bulletinId },
    });
    if (error) throw error;
  },
});
